// Global Instructions Rule Applied!

import { useCallback, useEffect, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { fetchFundWaterfallPosition } from '../services/fundsService.ts'
import { fetchFundGroupWaterfallPosition } from '../../fund_groups/services/fundGroupsService.ts'
import { FundWaterfallPosition } from '../../../state_manager/data_models/fund_waterfall_position_record.ts'

/**
 * Whose waterfall position: a fund's (GET /funds/:id/waterfall/position) or
 * a consolidated fund's (GET /fund-groups/:id/waterfall/position - the same
 * model, the consolidated fund as its fund_id; waterfall phase 5).
 */
export type WaterfallPositionScope = { fundId: string | null | undefined } | { fundGroupId: string | null | undefined }

/**
 * useFundWaterfallPosition — the waterfall position as at a date (each
 * partner's booked waterfall state and the hypothetical liquidation of its
 * capital account at fair market value: the accrued carried interest) for the
 * Statements tab panel, of a fund or of a consolidated fund. `position` is
 * null while there is no open waterfall; `loaded` says a read has come back.
 *
 * Section-local, like the fund waterfall and the statement runs: not in a
 * Redux slice.
 */
export function useFundWaterfallPosition(scope: WaterfallPositionScope, asOf: string) {
  const fundIdString = 'fundId' in scope && scope.fundId ? String(scope.fundId) : null
  const fundGroupIdString = 'fundGroupId' in scope && scope.fundGroupId ? String(scope.fundGroupId) : null

  const [position, setPosition] = useState<FundWaterfallPosition | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!fundIdString && !fundGroupIdString) {
      setPosition(null)
      setLoaded(false)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const accessToken = getAccessToken(store.getState().auth)
      const res = fundGroupIdString
        ? await fetchFundGroupWaterfallPosition(fundGroupIdString, asOf, accessToken)
        : await fetchFundWaterfallPosition(fundIdString as string, asOf, accessToken)
      if (res.error) {
        setError(res.error)
      } else {
        setPosition(res.data)
        setLoaded(true)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the waterfall position')
    } finally {
      setLoading(false)
    }
  }, [fundIdString, fundGroupIdString, asOf])

  useEffect(() => {
    load()
  }, [load])

  return { position, loaded, loading, error, refetch: load }
}
